import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const COMMUNITY_STATUS = {
  pending: { label: 'Pending', className: 'bg-amber-500/15 text-amber-200 border-amber-400/25' },
  approved: { label: 'Approved', className: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/25' },
  rejected: { label: 'Rejected', className: 'bg-rose-500/15 text-rose-200 border-rose-400/25' },
};

const AutoModBadge = ({ verdict }) => {
  if (!verdict) return null;
  const map = {
    safe: { label: 'AI: safe', className: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/25', Icon: ShieldCheck },
    warn: { label: 'AI: warn', className: 'bg-amber-500/15 text-amber-200 border-amber-400/25', Icon: ShieldAlert },
    unsafe: { label: 'AI: unsafe', className: 'bg-rose-500/15 text-rose-200 border-rose-400/25', Icon: ShieldX },
  };
  const v = map[verdict.verdict] || map.safe;
  const Icon = v.Icon;
  const conf = Math.round((verdict.confidence || 0) * 100);
  return (
    <div className="flex items-start gap-1.5">
      <Badge className={`border text-[10px] inline-flex items-center gap-1 ${v.className}`} data-testid="auto-mod-badge">
        <Icon className="w-3 h-3" /> {v.label} · {conf}%
      </Badge>
      {verdict.reason && (
        <p className="text-[10px]" style={{ color: 'var(--jw-text-subtle)' }} title={verdict.reason}>
          {verdict.reason.length > 60 ? `${verdict.reason.slice(0, 60)}…` : verdict.reason}
        </p>
      )}
    </div>
  );
};



export const CommunitySection = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [action, setAction] = useState('approve');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/admin/community-listings', { params: { status: tab } })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openReview = (it, a) => { setReviewing(it); setAction(a); setNotes(''); };

  const submitReview = async () => {
    if (!reviewing) return;
    try {
      await api.patch(`/admin/community-listings/${reviewing.id}/review`, { action, notes });
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      setReviewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-lg">Community Listings</h3>
          <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Review user-submitted designs. Approved listings appear in the public community feed.</p>
        </div>
        <div className="-mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
          <div className="px-4 sm:px-0 min-w-max">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="pending" data-testid="admin-community-tab-pending">Pending</TabsTrigger>
                <TabsTrigger value="approved" data-testid="admin-community-tab-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="admin-community-tab-rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all" data-testid="admin-community-tab-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="jw-card p-10 text-center" style={{ color: 'var(--jw-text-muted)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="jw-card p-10 text-center" style={{ color: 'var(--jw-text-muted)' }} data-testid="admin-community-empty">Nothing here.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const st = COMMUNITY_STATUS[it.status] || { label: it.status, className: 'bg-white/[0.06] text-white/80 border-white/10' };
            return (
              <Card key={it.id} className="jw-card overflow-hidden" data-testid={`admin-community-item-${it.id}`}>
                <div className="aspect-video overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                  <img src={assetUrl(it.preview_url)} alt={it.title} className="w-full h-full object-contain" />
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold truncate">{it.title}</h4>
                    <Badge className={`border text-[10px] ${st.className}`}>{st.label}</Badge>
                  </div>
                  {it.auto_moderation && (
                    <AutoModBadge verdict={it.auto_moderation} />
                  )}
                  {it.report_count > 0 && (
                    <div className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-200 border border-rose-400/25 self-start">
                      <Flag className="w-3 h-3" /> {it.report_count} report{it.report_count > 1 ? 's' : ''}
                    </div>
                  )}
                  <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
                    By <b>{it.owner?.name || 'Anonymous'}</b>{it.owner_email ? ` · ${it.owner_email}` : ''}
                    {(it.owner_publish_strikes || 0) > 0 && (
                      <span
                        data-testid={`admin-community-strikes-${it.id}`}
                        className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${it.owner_publish_muted ? 'bg-rose-500/20 text-rose-200 border-rose-400/40' : 'bg-amber-500/15 text-amber-200 border-amber-400/25'}`}
                        title={it.owner_publish_muted ? 'Publishing paused — repeat offender' : `${it.owner_publish_strikes} removed listing${it.owner_publish_strikes === 1 ? '' : 's'}`}
                      >
                        <ShieldAlert className="w-3 h-3" /> {it.owner_publish_strikes}/3{it.owner_publish_muted ? ' · muted' : ''}
                      </span>
                    )}
                  </p>
                  {it.description && <p className="text-xs line-clamp-2" style={{ color: 'var(--jw-text-subtle)' }}>{it.description}</p>}
                  <div className="flex gap-1 flex-wrap">
                    {it.category && <Badge variant="secondary" className="text-[10px] capitalize">{it.category.replace('_', ' ')}</Badge>}
                    {(it.tags || []).slice(0, 4).map((t) => (<Badge key={t} variant="secondary" className="text-[10px]">#{t}</Badge>))}
                  </div>
                  {it.moderation_notes && (
                    <p className="text-[11px] p-2 rounded border" style={{ color: 'var(--jw-text-muted)', borderColor: 'var(--jw-border)' }}>
                      <b>Notes:</b> {it.moderation_notes}
                    </p>
                  )}
                  {it.status !== 'approved' && (
                    <Button size="sm" onClick={() => openReview(it, 'approve')} data-testid={`admin-community-approve-${it.id}`} className="jw-btn-glossy h-8 mr-2">Approve</Button>
                  )}
                  {it.status !== 'rejected' && (
                    <Button size="sm" variant="secondary" onClick={() => openReview(it, 'reject')} data-testid={`admin-community-reject-${it.id}`} className="jw-btn-gloss-ghost h-8">Reject</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!reviewing} onOpenChange={(v) => !v && setReviewing(null)}>
        <DialogContent data-testid="admin-community-review-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{action === 'approve' ? 'Approve listing' : 'Reject listing'}</DialogTitle>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-3">
              <div className="flex gap-3 items-center">
                <img src={assetUrl(reviewing.preview_url)} alt="" className="w-16 h-16 rounded-md object-cover" />
                <div>
                  <p className="font-semibold">{reviewing.title}</p>
                  <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{reviewing.owner?.name}</p>
                </div>
              </div>
              <div>
                <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
                  {action === 'approve' ? 'Notes (optional)' : 'Reason (shown to the creator)'}
                </label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-community-notes-input"
                  placeholder={action === 'approve' ? 'Nice work!' : 'e.g. Please replace the copyrighted image and resubmit.'} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button onClick={submitReview} data-testid="admin-community-review-submit" className="jw-btn-glossy">
              {action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default CommunitySection;
